import { useRouter } from 'vue-router';

interface FooterLinkPatch {
  readonly anchorText: string;
  readonly routeName: string;
}

interface ResolvedFooterLinkPatch {
  readonly anchorText: string;
  readonly targetHref: string;
}

const FOOTER_LINK_PATCHES: readonly FooterLinkPatch[] = [
  { anchorText: 'Impressum', routeName: 'Imprint' },
  { anchorText: 'Nutzungsbedingungen', routeName: 'TermsOfUse' },
];

const findFooterAnchor = (anchorText: string): HTMLAnchorElement | null => {
  const footer = document.getElementById('piveau-footer');
  if (!footer) return null;
  const anchors = footer.querySelectorAll<HTMLAnchorElement>('a');
  return Array.from(anchors).find((a) => a.textContent?.trim() === anchorText) ?? null;
};

const patchAnchor = ({ anchorText, targetHref }: ResolvedFooterLinkPatch): boolean => {
  const anchor = findFooterAnchor(anchorText);
  if (!anchor) return false;
  if (anchor.getAttribute('href') !== targetHref) {
    anchor.setAttribute('href', targetHref);
  }
  return true;
};

/**
 * Patches the third-party piveau-footer "Impressum" and "Nutzungsbedingungen"
 * anchor hrefs, which are hardcoded inside @open-data-bayern/piveau-header-footer
 * and cannot be overridden via props. The hrefs are resolved via vue-router to
 * the internal routes. A MutationObserver keeps the patches applied across
 * footer re-renders.
 */
export const useFooterLinkPatch = (): void => {
  if (typeof window === 'undefined') return;
  const router = useRouter();
  const patches: ResolvedFooterLinkPatch[] = FOOTER_LINK_PATCHES
    // Need to filter as a guard, otherwise router.resolve will throw an error if the route does not exist
    .filter(({ routeName }) => router.hasRoute(routeName))
    .map(({ anchorText, routeName }) => ({
      anchorText,
      targetHref: router.resolve({ name: routeName }).href,
    }));
  if (!patches.length) return;
  const apply = (): void => {
    if (patches.every(patchAnchor)) return;
    requestAnimationFrame(apply);
  };
  requestAnimationFrame(apply);
  const observer = new MutationObserver(() => { patches.forEach(patchAnchor); });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
};
